import { useEffect, useMemo, useState } from "react"

import DialogProvider from "@components/dialog_provider"
import {
  AiEngineRoutingOverview,
  compareEngineVersion
} from "@domain/ai_engine"
import { getCustomerByIdQuery } from "@domain/customer/infos"
import { getCustomers } from "@domain/customer/list"
import useTranslate from "@hooks/use_translate"
import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import {
  Autocomplete,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { useQueries, useQuery } from "@tanstack/react-query"
import dayjs from "dayjs"

import { ReasonDialogRequest } from "./ReasonDialog"

type CustomerOption = { id: string; label: string }

type PinsSectionProps = {
  overview: AiEngineRoutingOverview
  ask: (request: ReasonDialogRequest) => void
  actions: {
    pin: (
      data: { customerId: string; version: string; capability?: string },
      reason: string
    ) => Promise<void>
    unpin: (
      customerId: string,
      capability: string | undefined,
      reason: string
    ) => Promise<void>
  }
}

const OBJECT_ID = /^[a-f\d]{24}$/i

const ALL = "__all__"

/** Pinned customers: the one control that lets a single tenant try a version before anyone else. */
export default function PinsSection({
  overview,
  ask,
  actions
}: PinsSectionProps) {
  const { translate } = useTranslate()
  const [open, setOpen] = useState(false)
  const pins = overview.routing.pins

  const customerIds = useMemo(
    () => Array.from(new Set(pins.map((pin) => pin.customerId))),
    [pins]
  )
  const customerNames = useQueries({
    queries: customerIds.map((customerId) => ({
      queryKey: ["ai-engine-pin-customer", customerId],
      staleTime: Infinity,
      queryFn: async () => {
        const response = await getCustomerByIdQuery(customerId)
        return response.data?.companyName || ""
      }
    }))
  })
  const nameOf = (customerId: string) =>
    customerNames[customerIds.indexOf(customerId)]?.data || customerId

  const capabilityLabel = (capability?: string | null) =>
    capability
      ? translate(`ai_engine_capability_${capability}`)
      : translate("ai_engine_all_capabilities")

  const removePin = (customerId: string, capability?: string | null) =>
    ask({
      title: translate("ai_engine_unpin_title"),
      summary: translate("ai_engine_unpin_summary").replace(
        "{customer}",
        nameOf(customerId)
      ),
      onConfirm: (reason) =>
        actions.unpin(customerId, capability ?? undefined, reason)
    })

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack gap={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Stack>
            <Typography variant="subtitle1" fontWeight={600}>
              {translate("ai_engine_pins_title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate("ai_engine_pins_desc")}
            </Typography>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
          >
            {translate("ai_engine_pin_add")}
          </Button>
        </Stack>

        {pins.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {translate("ai_engine_pins_empty")}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate("ai_engine_customer")}</TableCell>
                <TableCell>{translate("ai_engine_capability")}</TableCell>
                <TableCell>{translate("ai_engine_version")}</TableCell>
                <TableCell>{translate("ai_engine_by")}</TableCell>
                <TableCell>{translate("ai_engine_at")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {pins.map((pin) => (
                <TableRow key={`${pin.customerId}:${pin.capability ?? ALL}`}>
                  <TableCell>
                    <Typography variant="body2">
                      {nameOf(pin.customerId)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {pin.customerId}
                    </Typography>
                  </TableCell>
                  <TableCell>{capabilityLabel(pin.capability)}</TableCell>
                  <TableCell>
                    <Chip size="small" color="primary" label={pin.version} />
                  </TableCell>
                  <TableCell>{pin.createdBy}</TableCell>
                  <TableCell>
                    {dayjs(pin.createdAt).format("DD.MM.YYYY HH:mm")}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={translate("ai_engine_unpin")}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          removePin(pin.customerId, pin.capability)
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>

      <AddPinDialog
        isOpen={open}
        onClose={() => setOpen(false)}
        overview={overview}
        onSubmit={({ customerName, ...data }) => {
          setOpen(false)
          ask({
            title: translate("ai_engine_pin_title"),
            summary: translate("ai_engine_pin_summary")
              .replace("{customer}", customerName)
              .replace("{version}", data.version)
              .replace("{capability}", capabilityLabel(data.capability)),
            onConfirm: (reason) => actions.pin(data, reason)
          })
        }}
      />
    </Paper>
  )
}

function AddPinDialog({
  isOpen,
  onClose,
  overview,
  onSubmit
}: {
  isOpen: boolean
  onClose: () => void
  overview: AiEngineRoutingOverview
  onSubmit: (data: {
    customerId: string
    customerName: string
    version: string
    capability?: string
  }) => void
}) {
  const { translate } = useTranslate()
  const versions = useMemo(
    () =>
      overview.engines
        .map((engine) => engine.version)
        .sort(compareEngineVersion),
    [overview.engines]
  )
  const [search, setSearch] = useState("")
  const [debounced, setDebounced] = useState("")
  const [customer, setCustomer] = useState<CustomerOption | null>(null)
  const [version, setVersion] = useState(versions[0] ?? "")
  const [capability, setCapability] = useState(ALL)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 350)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (isOpen) {
      setSearch("")
      setCustomer(null)
      setVersion(versions[0] ?? "")
      setCapability(ALL)
    }
  }, [isOpen, versions])

  const { data: options = [], isLoading } = useQuery({
    queryKey: ["ai-engine-pin-customers", debounced],
    enabled: isOpen,
    queryFn: async (): Promise<CustomerOption[]> => {
      const response = await getCustomers({
        pageNumber: 1,
        pageSize: 20,
        search: debounced
      })
      const byId = new Map<string, CustomerOption>()
      for (const doc of response.data.docs ?? []) {
        const id = String(doc.customer?._id ?? "")
        if (OBJECT_ID.test(id) && !byId.has(id)) {
          byId.set(id, { id, label: String(doc.customer?.companyName || id) })
        }
      }
      return Array.from(byId.values())
    }
  })

  const valid = !!customer && !!version

  return (
    <DialogProvider
      title={translate("ai_engine_pin_add")}
      isOpen={isOpen}
      setClose={onClose}
      size="medium"
      leftButton={{
        text: translate("ai_engine_cancel"),
        color: "secondary",
        onClick: onClose
      }}
      rightButton={{
        text: translate("ai_engine_continue"),
        color: "primary",
        disabled: !valid,
        onClick: () => {
          if (!customer) return
          onSubmit({
            customerId: customer.id,
            customerName: customer.label,
            version,
            ...(capability !== ALL && { capability })
          })
        }
      }}
    >
      <Stack gap={2} pt={1}>
        <Typography variant="body2" color="text.secondary">
          {translate("ai_engine_pin_dialog_desc")}
        </Typography>
        <Autocomplete<CustomerOption>
          options={options}
          loading={isLoading}
          value={customer}
          inputValue={search}
          filterOptions={(list) => list}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          onInputChange={(_event, value) => setSearch(value)}
          onChange={(_event, value) => setCustomer(value)}
          getOptionLabel={(option) => option.label}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Stack>
                <Typography variant="body2">{option.label}</Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace" }}
                >
                  {option.id}
                </Typography>
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label={translate("ai_engine_customer")} />
          )}
        />
        <Stack direction="row" gap={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>{translate("ai_engine_version")}</InputLabel>
            <Select
              label={translate("ai_engine_version")}
              value={version}
              onChange={(event) => setVersion(String(event.target.value))}
            >
              {versions.map((entry) => (
                <MenuItem key={entry} value={entry}>
                  {entry}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {overview.capabilities.length > 1 && (
            <FormControl size="small" fullWidth>
              <InputLabel>{translate("ai_engine_capability")}</InputLabel>
              <Select
                label={translate("ai_engine_capability")}
                value={capability}
                onChange={(event) => setCapability(String(event.target.value))}
              >
                <MenuItem value={ALL}>
                  {translate("ai_engine_all_capabilities")}
                </MenuItem>
                {overview.capabilities.map((entry) => (
                  <MenuItem key={entry} value={entry}>
                    {translate(`ai_engine_capability_${entry}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Stack>
    </DialogProvider>
  )
}
